import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useSubmitFeedback } from '@/hooks/useFeedback';
import { trackConversion } from '@/lib/analytics';
import { maskCustomerId } from '@/lib/maskCustomerId';
import { formatMicros, shareWithoutAds, type AdsFeedSummary } from '@/lib/adsFeedSummary';
import { formatInteger } from '@/lib/language';

export function adsInterestStorageKey(merchantCenterId: string): string {
  return `fo:ads-interest:${merchantCenterId}`;
}

function readReported(merchantCenterId: string | null): boolean {
  if (!merchantCenterId) return false;
  try {
    return localStorage.getItem(adsInterestStorageKey(merchantCenterId)) !== null;
  } catch {
    return false;
  }
}

/**
 * "Interesse melden" im Teaser (Spec 5): die Meldung IST eine Zeile in
 * `feedback` (Typ feedback), der bestehende Trigger trg_notify_feedback_report
 * pingt den Reports-Bot. Kein neuer Backend-Pfad. Der lokale Marker haelt den
 * Knopf nach Reload weg; Server-Zustand gibt es bewusst nicht.
 */
export function useAdsInterest(input: {
  merchantCenterId: string | null;
  merchantCenterName: string | null;
  feedName: string | null;
  feedLanguage: string | null;
  adsCustomerId: string | null;
  summary: AdsFeedSummary | null;
  currency: string | null;
}) {
  const submit = useSubmitFeedback();
  const [reported, setReported] = useState(() => readReported(input.merchantCenterId));

  // Der Marker haengt AM MERCHANT CENTER, der lazy Initialisierer laeuft aber
  // nur beim ersten Mount. Ohne dieses Nachlesen behielte ein Kontowechsel den
  // Zustand des vorherigen Kontos: wer fuer MC A gemeldet hat, saehe den Knopf
  // fuer MC B nicht mehr. Der Initialisierer bleibt, damit der erste Aufbau
  // nicht erst false zeigt und dann umspringt.
  useEffect(() => {
    setReported(readReported(input.merchantCenterId));
  }, [input.merchantCenterId]);

  const report = useCallback(async () => {
    // Doppelklick-Guard: der Knopf verschwindet erst NACH dem Schreiben, und
    // zwei Klicks waeren zwei Zeilen in `feedback` plus zwei Reports-Bot-Pings.
    if (reported || submit.isPending) return;
    const s = input.summary;
    const lines = [
      `Merchant Center: ${input.merchantCenterName ?? '?'}`,
      `Feed: ${input.feedName ?? '?'}${input.feedLanguage ? ` (${input.feedLanguage})` : ''}`,
      `Ads-Konto: ${input.adsCustomerId ? maskCustomerId(input.adsCustomerId) : 'nicht verbunden'}`,
      s
        ? `Ohne Anzeigen: ${formatInteger(s.products_without_ads, 'de')} von ${formatInteger(s.products_total, 'de')} (${shareWithoutAds(s)} %)`
        : null,
      s ? `Kosten 28 Tage: ${formatMicros(s.cost_micros, input.currency, 'de')}` : null,
    ].filter((l): l is string => l !== null);
    try {
      await submit.mutateAsync({
        type: 'feedback',
        title: 'Interesse: Segmente als Kampagnen',
        description: lines.join('\n').slice(0, 2000),
        page_url: '/',
      });
      if (s) trackConversion('ads_segments_interest_click', { share_without_ads: shareWithoutAds(s), products_total: s.products_total });
      if (input.merchantCenterId) {
        try {
          localStorage.setItem(adsInterestStorageKey(input.merchantCenterId), new Date().toISOString());
        } catch {
          /* ohne Speicher zeigt der Reload den Knopf erneut */
        }
      }
      setReported(true);
    } catch (error) {
      // Ein Fehlschlag darf den Knopf NICHT als erledigt markieren (weder Marker
      // noch Ereignis noch reported), und `report()` darf nie ablehnen: der
      // Aufrufer ist ein onClick, eine abgelehnte Promise waere dort ein
      // unbehandelter Fehler. Der Nutzer sieht den Fehler ueber den Toast aus
      // `useSubmitFeedback`; still ist das hier trotzdem nicht.
      Sentry.captureException(error, {
        // `ads-chapter` wie die uebrigen Meldungen dieses Kapitels
        // (`useAdsFeedSummary`, `useAdsConnectFlow`, `ads-first-pull`).
        // `ads-performance` gehoert dem internen Panel; zwei Namen fuer
        // dieselbe Flaeche trennen in Sentry, was zusammengehoert.
        tags: { feature: 'ads-chapter', hook: 'useAdsInterest' },
      });
    }
  }, [input, submit, reported]);

  return { reported, report, isReporting: submit.isPending };
}
